import classNames from "classnames";
import clsx from "clsx";
import PropTypes from "prop-types";
import React, { useState } from "react";
import { Accordion, AccordionDetails, AccordionSummary } from "@mui/material";
import { ColorVariantType } from "../../../types";
import { Disclosure } from "@headlessui/react";
import { getAbsoluteImagePath } from "@utils/dom";
import { KeyboardArrowDownOutlined } from "@mui/icons-material";
import { useConfig } from "@hooks";

interface DisclosureProps {
  title: string | React.ReactNode;
  titleOpen?: string | React.ReactNode;
  variant?: ColorVariantType["variant"] | "transparent" | "white";
  defaultOpen?: boolean;
  showArrow?: boolean;
  showFleche?: boolean;
}

const MyDisclosure: React.FC<DisclosureProps> = ({
  title,
  titleOpen,
  variant = "primary",
  defaultOpen = false,
  showArrow = false,
  showFleche = false,
  children,
}) => {
  const { mode: clientMode } = useConfig();
  const mode = clientMode ? clientMode : "vacanceole";

  const DISCLORSURE_CLASS = {
    base: "tw-flex tw-justify-between tw-items-center tw-w-full tw-font-semibold tw-p-2 tw-outline-none",
    variant: {
      default: "tw-text-white tw-bg-gray-300",
      primary: `tw-text-white ${
        mode === "vacanceole" ? "tw-bg-v_blue-dark" : "tw-bg_corseole-secondary"
      }`,
      secondary: "tw-text-white tw-bg-secondary-500",
      info: "tw-text-white tw-bg-blue-500",
      success: "tw-text-white tw-bg-green-500",
      warning: "tw-text-white tw-bg-yellow-500",
      danger: "tw-text-white tw-bg-red-500",
      transparent: "tw-text-black",
      white: "tw-text-black tw-bg-white",
    },
    buttonOpen: "tw-rounded-b-none",
    arrowIcon: {
      base: "tw-w-5 tw-h-5",
      default: "tw-text-gray-400",
      primary: "tw-text-white",
      transparent: "tw-text-black",
    },
    arrowIconOpen: "tw-transform tw-rotate-180",
    panel: {
      base: "tw-border-none tw-rounded-t-none tw-p-2 tw-relative tw--top-1",
      variant: {
        default: "tw-border-gray-300",
        primary: `${
          mode === "vacanceole"
            ? "tw-border-v_blue-dark"
            : "tw-border-primary_corseole-500"
        }`,
        secondary: "tw-border-secondary-500",
        info: "tw-border-blue-500",
        success: "tw-border-green-500",
        warning: "tw-border-yellow-500",
        danger: "tw-border-red-500",
        transparent: "tw-border-0 tw-border-none",
        white: "",
      },
    },
  };

  return (
    <div>
      <Disclosure defaultOpen={defaultOpen}>
        {({ open }) => (
          <>
            <Disclosure.Button
              className={classNames(
                DISCLORSURE_CLASS.base,
                DISCLORSURE_CLASS.variant[variant],
                open && DISCLORSURE_CLASS.buttonOpen
              )}
            >
              <span>
                {open && titleOpen ? titleOpen : title}
                {!open && showFleche && (
                  <img
                    className="tw-w-8 tw-inline-block tw-ml-2"
                    src={getAbsoluteImagePath(
                      `/assets/img/icons/fleche${
                        variant === "primary" ? "_white" : ""
                      }.svg`
                    )}
                  ></img>
                )}
              </span>
              {(open || showArrow) && (
                <img
                  src={getAbsoluteImagePath(
                    `/assets/img/icons/carret_down${
                      variant === "primary" ? "_white" : ""
                    }.svg`
                  )}
                  className={classNames(
                    DISCLORSURE_CLASS.arrowIcon.base,
                    variant === "transparent"
                      ? DISCLORSURE_CLASS.arrowIcon.transparent
                      : variant === "primary"
                      ? DISCLORSURE_CLASS.arrowIcon.primary
                      : DISCLORSURE_CLASS.arrowIcon.default,
                    open && DISCLORSURE_CLASS.arrowIconOpen
                  )}
                ></img>
              )}
            </Disclosure.Button>
            <Disclosure.Panel
              className={classNames(
                DISCLORSURE_CLASS.panel.base,
                DISCLORSURE_CLASS.panel.variant[variant]
              )}
            >
              {children}
            </Disclosure.Panel>
          </>
        )}
      </Disclosure>
    </div>
  );
};

type MyAccordionProps = {
  title: (open: boolean) => string | JSX.Element;
  open?: boolean;
};

export const MyAccordion: React.FC<MyAccordionProps> = ({
  title,
  open = true,
  children,
}) => {
  /* State */
  const [expanded, setExpanded] = useState(open);

  /* Render */
  return (
    <Accordion
      expanded={expanded}
      onChange={() => setExpanded(!expanded)}
      style={{ backgroundColor: "transparent" }}
      className={clsx(
        "!tw-rounded-lg !tw-shadow-none",
        expanded && "!tw-shadow-lg",
        "custom-accordtion"
      )}
    >
      <AccordionSummary
        classes={{ content: "!tw-m-0" }}
        aria-controls="panel_logind-content"
        id="panel_logind-header"
        className={clsx(
          expanded
            ? "!tw-border !tw-border-solid !tw-border-v_blue-dark !tw-border-b-0 !tw-text-v_blue-dark !tw-rounded-t-lg"
            : "!tw-text-v_blue-dark !tw-border !tw-border-solid !tw-border-v_blue-dark !tw-rounded-lg",
          "!tw-min-h-[64px]"
        )}
        style={{ border: "inherit" }}
      >
        <div className="tw-flex tw-items-center tw-w-full tw-justify-between">
          <span className="">{title(expanded)}</span>
          <KeyboardArrowDownOutlined
            className={clsx(
              expanded ? "tw-rotate-180" : "",
              "!tw-fill-v_blue-dark"
            )}
          />
        </div>
      </AccordionSummary>
      <AccordionDetails
        style={{ backgroundColor: "white" }}
        className="!tw-rounded-b-lg !tw-p-4 !tw-border !tw-border-solid !tw-border-v_blue-dark !tw-border-t-0"
      >
        {children}
      </AccordionDetails>
    </Accordion>
  );
};

MyDisclosure.propTypes = {};

export default MyDisclosure;
